// React Context for workflow form state
import { createContext, useContext } from "react";
import type { ReactNode } from "react";
import type {
  FieldValue,
  FormErrors,
  FormState,
} from "../../../shared/types/workflow/workflow-form.types";
import type { Transition } from "../../../shared/types/workflow/workflow-form.types";
import { useWorkflowForm } from "../hooks/use-workflow-form";

interface WorkflowFormContextType {
  formState: FormState;
  errors: FormErrors;
  touched: Set<string>;
  isSubmitting: boolean;
  setIsSubmitting: (isSubmitting: boolean) => void;
  updateField: (name: string, value: FieldValue) => void;
  touchField: (name: string) => void;
  validateField: (
    name: string,
    touchedSet?: Set<string>,
    fieldValue?: FieldValue,
  ) => Promise<boolean>;
  validateForm: () => Promise<boolean>;
  resetForm: () => void;
  schema: Transition;
  companyId: string;
  selectedCount?: number;
}

export const WorkflowFormContext =
  createContext<WorkflowFormContextType | null>(null);

export const useWorkflowFormContext = () => useContext(WorkflowFormContext);

interface WorkflowFormProviderProps {
  schema: Transition;
  companyId: string;
  initialValues?: Partial<FormState>;
  children: ReactNode;
  selectedCount?: number;
}

export function WorkflowFormProvider({
  schema,
  companyId,
  initialValues,
  children,
  selectedCount,
}: WorkflowFormProviderProps) {
  const form = useWorkflowForm(schema, initialValues);
  return (
    <WorkflowFormContext.Provider
      value={{ ...form, schema, companyId, selectedCount }}
    >
      {children}
    </WorkflowFormContext.Provider>
  );
}
